import {
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
    Select,
    MenuItem,
    Divider,
    Grid,
    Button,
    Box,
    CircularProgress,
    FormControl
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { meregeSimiliarRoles } from '../../lib/user-helpers';
import {
    AssociatedSiteRole,
    RoleLocationList,
    SelectedOrgInfo,
    SiteInfo,
    SiteRole,
    SiteRoleData,
    Status,
    UserRole,
} from '../../types';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import userStyles from '../../app/user-management/UserStyle';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { useDispatch, useSelector } from 'react-redux';
import { UserSettingsThunks } from '../../actions/thunks/user-settings-thunks';
import { useAuthHelper } from '../AuthContextProvider';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import * as PXBColors from '@brightlayer-ui/colors';
import { Selectors } from '../../selectors';
import _, { cloneDeep } from 'lodash';
import { DemoteOrgAdmin } from '../DemoteOrgAdmin/DemoteOrgAdmin';
import { RoleLocation } from '../RoleLocation/RoleLocation';
import { isRecordFound, useUserRoles } from '../../hooks/data-hooks';
import { StatusTag } from '../StatusTag/StatusTag';

type UpdatedRole = Array<{
    roleId: string;
    siteIds: string[];
}>;

const useStyles = makeStyles({
    '@global': {
        // You should target [class*="MuiButton-root"] instead if you nest themes.
        '.MuiListItem-root:hover': {
            backgroundColor: 'rgba(0, 0, 0,0)',
        },
    },
    hover: {},
});

export const UpdateUserLocation: React.FC<{
    key: string;
    userId: string;
    orgLocationInfo: SelectedOrgInfo;
    onCancelEdit: Function;
    roleLocationList: SiteRoleData[];
    registrationRole: Array<{
        roleId: string;
        roleName: string;
    }>;
    isDirty: Function;
}> = (props) => {
    const classes1 = useStyles();
    const { t } = useLanguageLocale();
    const authHelper = useAuthHelper();
    const dispatch = useDispatch();
    const classes = userStyles();
    const { addToast } = useToasts();
    const [modifiedRoleData, setModifiedRoleData] = useState<AssociatedSiteRole[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [submitProgress, setSubmitProgress] = useState(false);
    const [disableButton, setDisableButton] = useState(true);
    const [promoteOrgAdmin, setPromoteOrgAdmin] = useState(false);
    const [existingLocation, setExistingLocation] = useState<SiteInfo[]>([]);

    const [hasOrgAdminRole, setOrgAdminRole] = useState(false);
    const [unmodifiedRoleData, setUnmodifiedRoleData] = useState<AssociatedSiteRole[]>([]);

    const userRoleState = useSelector(Selectors.User.user);
    const loggedInRoleData = useUserRoles();
    const [addRoleLocationList, setAddRoleLocationList] = useState<RoleLocationList>([
        {
            index: 0,
            roleId: '0',
            disabledLocations: [],
            roleList: props.registrationRole.filter((x) => x.roleName !== UserRole.Unassigned),
            selectedLocation: [{ siteId: '', siteName: '' }],
        },
    ]);
    useEffect(() => {
        if (userRoleState.updateRoleLoading && !userRoleState.updateRoleSuccess) {
            setSubmitProgress(true);
        }
        if (!userRoleState.updateRoleLoading && userRoleState.updateRoleSuccess) {
            props.onCancelEdit(false);
        }
        if (!userRoleState.updateRoleLoading && !userRoleState.updateRoleSuccess) {
            setSubmitProgress(false);
        }
    }, [userRoleState]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedRoleData) !== JSON.stringify(modifiedRoleData);
        const newRole = addRoleLocationList.every((a) => a.roleId !== '0');
        setIsDirty(newRole || isDataModified);
        props.isDirty(newRole || isDataModified);
    }, [unmodifiedRoleData, modifiedRoleData, addRoleLocationList]); //eslint-disable-line react-hooks/exhaustive-deps

    const isLocationExist = (siteId: string): boolean => {
        const hasLocation = props.orgLocationInfo.locations.filter((x) => x.locationId === siteId);
        return hasLocation.length > 0;
    };
    const convertRoleLocationEditFormat = (
        roleName: UserRole,
        roleLocationList: SiteRoleData[],
        loggedInRole: UserRole
    ): AssociatedSiteRole => {
        const roleByRoleName = roleLocationList.filter((x: SiteRoleData) => x.roleName === roleName);
        return {
            roleId: roleByRoleName.length > 0 ? roleByRoleName[0].roleId : '',
            siteIds: roleByRoleName.map((x) => ({
                siteId: x.siteId,
                siteName: x.siteName,
                disabled: !isLocationExist(x.siteId),
            })),
            disabled: roleByRoleName.length > 0 && loggedInRole === roleByRoleName[0].roleName,
        };
    };

    const associatedLocation = (existingRoleLocationData: AssociatedSiteRole[]): SiteInfo[] =>
        _.flatten(
            existingRoleLocationData.map((role: AssociatedSiteRole) =>
                role.siteIds.map((site: SiteInfo) => ({
                    siteId: site.siteId,
                    siteName: site.siteName,
                }))
            )
        );

    useEffect(() => {
        if (props.roleLocationList.length > 0 && isRecordFound(loggedInRoleData)) {
            const loggedInUserRoleValue = loggedInRoleData.value.find((x) => x.roleName === UserRole.LocationAdmin)
                ? UserRole.LocationAdmin
                : loggedInRoleData.value[0].roleName;
            const orgObj = convertRoleLocationEditFormat(
                UserRole.OrgAdmin,
                props.roleLocationList,
                loggedInUserRoleValue as UserRole
            );
            const locationObj = convertRoleLocationEditFormat(
                UserRole.LocationAdmin,
                props.roleLocationList,
                loggedInUserRoleValue as UserRole
            );
            const siteObj = convertRoleLocationEditFormat(
                UserRole.SiteUser,
                props.roleLocationList,
                loggedInUserRoleValue as UserRole
            );

            let newArray: AssociatedSiteRole[] = [];
            if (orgObj.roleId !== '') {
                setOrgAdminRole(true);
            } else {
                newArray.push(locationObj);
                newArray.push(siteObj);
                setOrgAdminRole(false);
            }
            newArray = newArray.filter((x) => x.roleId !== '');
            const unmodifiedArray = cloneDeep(newArray);
            setUnmodifiedRoleData(unmodifiedArray);
            setModifiedRoleData(newArray);
            const associtedLocations: SiteInfo[] = associatedLocation(newArray);
            setExistingLocation(associtedLocations);
        }
    }, [props.roleLocationList]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const validExistingData = JSON.stringify(unmodifiedRoleData) === JSON.stringify(modifiedRoleData);
        if (!validExistingData) setDisableButton(validExistingData);

        if (addRoleLocationList.every((a) => a.roleId !== '0')) {
            const validAddNewRole = addRoleLocationList.every((b) => b.roleId !== '' && b.selectedLocation.length > 0);
            setDisableButton(!validAddNewRole);
        }
    }, [props.roleLocationList, modifiedRoleData, addRoleLocationList, unmodifiedRoleData]);

    const onRoleChange = (value: string, index: number): void => {
        const obj = [...modifiedRoleData];
        obj[index].roleId = value;
        setModifiedRoleData(obj);
    };
    const handleDeleteChips = (
        chipList: Array<{ siteId: string; siteName: string; disabled: boolean }>,
        chipToDelete: SiteInfo,
        index: number
    ): void => {
        const obj = [...modifiedRoleData];
        const result = chipList.filter((chip: SiteInfo) => chip.siteId !== chipToDelete.siteId);
        obj[index].siteIds = result;
        setModifiedRoleData(obj);
        /** disable already assocaited location */
        const associtedLocation: SiteInfo[] = associatedLocation(obj);
        setExistingLocation(associtedLocation);
        const value: SiteInfo[] = _.flatten(addRoleLocationList.map((x) => x.selectedLocation));

        const updateDisableInAdd = [...addRoleLocationList];
        const update = updateDisableInAdd.map((x) => {
            const disable = [...associtedLocation, ...value];
            return {
                index: x.index,
                roleId: x.roleId,
                disabledLocations: disable.filter((y) => !x.selectedLocation.includes(y)),
                roleList: x.roleList,
                selectedLocation: x.selectedLocation,
            };
        });
        setAddRoleLocationList(update);
    };

    const addRoleChange = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
        const selectedAddRole = data.length > 0 && props.registrationRole.find((a) => a.roleId === data[0].roleId);
        if (selectedAddRole && selectedAddRole.roleName === UserRole.OrgAdmin) {
            setPromoteOrgAdmin(true);
        } else setPromoteOrgAdmin(false);
    };
    const removeAdditionalRow = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
    };
    const onSelectedLocation = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
    };
    const onAddRow = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
    };
    const demoteRoleChange = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
    };
    const demoteLocationSelected = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
    };
    const demoteAddRow = (data: RoleLocationList): void => {
        setAddRoleLocationList(data);
    };

    const onUpdateRoles = (): void => {
        let updateRoles: SiteRole[] = [];
        /** add new role - location array */
        const addRole: UpdatedRole = addRoleLocationList.map((z) => ({
            roleId: z.roleId,
            siteIds: z.selectedLocation.map((a: { siteId: string; siteName: string }) => a.siteId),
        }));
        /** filter the existing role location for disable one and role id is blank for blank add roles */
        const UpdatedRoleLocation: UpdatedRole = modifiedRoleData
            .filter((x) => x.disabled === false && x.roleId !== '' && x.siteIds.length > 0)
            .map((y: AssociatedSiteRole) => ({
                roleId: y.roleId,
                siteIds: y.siteIds.filter((z) => !z.disabled).map((b) => b.siteId),
            }));
        /** get select role to check user is org admin or not */
        const selectedRole = addRole.length === 1 && props.registrationRole.find((a) => a.roleId === addRole[0].roleId);
        /** Promote the user to org admin OR delete all the existing location */
        if (
            (selectedRole && selectedRole.roleName === UserRole.OrgAdmin) ||
            UpdatedRoleLocation.every((b) => b.siteIds.length === 0)
        )
            updateRoles = [...addRole];
        else {
            updateRoles = addRole.every((a) => a.roleId === '0')
                ? [...UpdatedRoleLocation]
                : [...UpdatedRoleLocation, ...addRole];
        }

        /** condition of unassigned role no role is there only default add role is there */
        if (updateRoles.length === 1 && updateRoles[0].roleId === '0') {
            const unAssignedId = props.registrationRole.find((a) => a.roleName === UserRole.Unassigned);
            if (unAssignedId) {
                updateRoles = [{ roleId: unAssignedId.roleId, siteIds: [props.orgLocationInfo.organizationId] }];
            }
        }
        /** merege similar role id into one object with site array */
        const updatedRoleData = meregeSimiliarRoles(updateRoles.filter((x) => x.roleId !== '0'));
        props.isDirty(false);
        dispatch(UserSettingsThunks.updateUserRole(props.userId, updatedRoleData, authHelper, addToast));
    };

    return (
        <>
            <Paper style={{ padding: 10 }} data-testid="organization-card">
                <List
                    subheader={
                        <ListItem key="ww" divider={true} style={{ backgroundColor: 'transparent' }}>
                            <ListItemIcon data-testid="business-icon">
                                <BusinessIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" data-testid="organization-name">
                                        {props.orgLocationInfo.organizationName}
                                    </Typography>
                                }
                            ></ListItemText>
                        </ListItem>
                    }
                >
                    {promoteOrgAdmin && (
                        <>
                            <Typography
                                variant="body1"
                                color="error"
                                style={{ margin: 20 }}
                                data-testid="promote-message1"
                            >
                                {t('EDITUSER.PROMOTE')}
                            </Typography>
                            <Typography
                                variant="body1"
                                color="error"
                                style={{ margin: 20 }}
                                data-testid="promote-message2"
                            >
                                {t('EDITUSER.PROMOTE_CONFIRMATION')}
                            </Typography>
                            <Divider />
                        </>
                    )}
                    {hasOrgAdminRole && (
                        <DemoteOrgAdmin
                            roleData={props.registrationRole.filter(
                                (x) => x.roleName !== UserRole.Unassigned && x.roleName !== UserRole.EatonAdmin
                            )}
                            userRoleId={props.roleLocationList[0].roleId}
                            roleChange={demoteRoleChange}
                            selectedLocation={demoteLocationSelected}
                            addRow={demoteAddRow}
                        />
                    )}
                    {!hasOrgAdminRole && (
                        <div>
                            {!promoteOrgAdmin && modifiedRoleData.length > 0 && (
                                <List disablePadding key="oo">
                                    {modifiedRoleData.map(
                                        (role: AssociatedSiteRole, index: number) =>
                                            role.siteIds.length > 0 && (
                                                <div key={index}>
                                                    <ListItem
                                                        key={index}
                                                        component="div"
                                                        classes={{ selected: classes1.hover }}
                                                        style={{ display: 'flex', flexWrap: 'wrap' }}
                                                    >
                                                        {role.siteIds.map((
                                                            location: any // eslint-disable-line @typescript-eslint/no-explicit-any
                                                        ) => (
                                                            <div key={location.siteId}>
                                                                {(role.disabled || location.disabled) && (
                                                                    <Chip
                                                                        data-testid={`location-chip${index}`}
                                                                        key={location.siteId}
                                                                        style={{ marginRight: 10, marginBottom: 10 }}
                                                                        id={`disabled${location.siteId}`}
                                                                        label={location.siteName}
                                                                        color="default"
                                                                    />
                                                                )}
                                                                {!role.disabled && !location.disabled && (
                                                                    <Chip
                                                                        key={location.siteId}
                                                                        style={{ marginRight: 10, marginBottom: 10 }}
                                                                        id={`chip${location.siteId}`}
                                                                        label={location.siteName}
                                                                        onDelete={(): void => {
                                                                            handleDeleteChips(
                                                                                role.siteIds,
                                                                                location,
                                                                                index
                                                                            );
                                                                        }}
                                                                        color="primary"
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </ListItem>
                                                    <ListItem key={`${role.roleId}${index}`}>
                                                        <Box
                                                            width="100%"
                                                            flexDirection="row"
                                                            alignItems="center"
                                                            display="flex"
                                                        >
                                                            <PersonIcon className={classes.IconStyle}></PersonIcon>
                                                            {role.disabled && (
                                                                <Box width="405px">
                                                                    <Typography variant="body1">
                                                                        {'Location Admin'}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {!role.disabled && (
                                                                <FormControl variant="standard" style={{ width:'860px' }}>
                                                                <Select
                                                                    label="Role"
                                                                    value={role.roleId}
                                                                    inputProps={{
                                                                        'data-testid': 'role',
                                                                    }}

                                                                    style={{ width: '60%', }}
                                                                    className={classes.addUserformControl}
                                                                    onChange={(e): void => {
                                                                        onRoleChange(e.target.value, index);
                                                                    }}
                                                                >
                                                                    {props.registrationRole
                                                                        .filter(
                                                                            (x) =>
                                                                                x.roleName !== UserRole.Unassigned &&
                                                                                x.roleName !== UserRole.OrgAdmin &&
                                                                                x.roleName !== UserRole.EatonAdmin
                                                                        )
                                                                        .map((roleInfo) => (
                                                                            <MenuItem
                                                                                key={roleInfo.roleId}
                                                                                value={roleInfo.roleId}
                                                                            >
                                                                                {roleInfo.roleName}
                                                                            </MenuItem>
                                                                        ))}
                                                                </Select>
                                                                </FormControl>
                                                            )}
                                                            <Box
                                                                width="100%"
                                                                display="flex"
                                                                flexDirection="row"
                                                                justifyContent="flex-end"
                                                                style={{ paddingRight: 5 }}
                                                                data-testid="user-status"
                                                            >
                                                                <StatusTag
                                                                    status={Status.Active}
                                                                    style={{
                                                                        float: 'left',
                                                                        marginRight: 10,
                                                                        lineHeight: '1.2'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </ListItem>
                                                    <Divider />
                                                </div>
                                            )
                                    )}
                                </List>
                            )}
                        </div>
                    )}
                </List>
                {!hasOrgAdminRole && (
                    <Box style={{ margin: 20 }}>
                        <RoleLocation
                            roleData={props.registrationRole.filter(
                                (x) => x.roleName !== UserRole.Unassigned && x.roleName !== UserRole.EatonAdmin
                            )}
                            onRoleChange={addRoleChange}
                            removeAddRow={removeAdditionalRow}
                            onSelectLocation={onSelectedLocation}
                            existingLocation={existingLocation}
                            isEdit={true}
                            AddRow={onAddRow}
                        ></RoleLocation>
                    </Box>
                )}

                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', margin: 20 }}>
                    <Button
                        variant="outlined"
                        onClick={(): void => {
                            props.onCancelEdit(isDirty);
                        }}
                        color="primary"
                        style={{ marginRight: 25 }}
                        data-testid="cancel-role-update"
                    >
                        Cancel
                    </Button>

                    {!submitProgress && (
                        <Button
                            disabled={disableButton}
                            data-testid="submit-role-update"
                            variant="contained"
                            color="primary"
                            onClick={onUpdateRoles}
                        >
                            Submit Changes
                        </Button>
                    )}
                    {submitProgress && (
                        <Button
                            variant="contained"
                            disabled
                            startIcon={<CircularProgress size="1rem" style={{ color: 'white' }} />}
                            style={{
                                height: '40px',
                                marginRight: 15,
                                backgroundColor: PXBColors.blue[200],
                                color: 'white',
                            }}
                        >
                            Submit Changes
                        </Button>
                    )}
                </Grid>
            </Paper>
        </>
    );
};
